import CasesApi from "@/api/cases";
import {
  CaseFileFlyout,
  CaseFilesProvider,
  CaseFilesTable,
  CaseFilesTableRef,
  CreateCaseFolderModal,
  File as CaseFile,
  FilesAPI,
  UploadProgress,
  useCaseFiles,
  EditCaseFileModal,
  DeleteCaseFileModal,
  ShareItemModal,
  BreadCrumbs,
} from "@/Features";
import { useQuery } from "@tanstack/react-query";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LoggingAPI from "@/api/logging";
import { EvidenceFilesTreeBanner } from "@/Features/EvidenceFiles/EvidenceFilesTreeBanner";

const CaseDocumentsWrapper: React.FC<{
  caseInfo: {
    case_id: number;
    uuid: string;
  };
}> = ({ caseInfo }) => {
  const { workspace, currentUser } = useAuth();
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showEditFileModal, setShowEditFileModal] = useState(false);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CaseFile | null>(null);

  const closeDeleteFileModal = () => {
    setShowDeleteFileModal(false);
    setSelectedItem(null);
  };

  const {
    folder_id,
    handleFileClick,
    uploadFolder,
    uploadFiles,
    tableRef,
    setSearch,
    search,
  } = useCaseFiles();

  const handleCreateFolder = () => {
    setShowCreateFolderModal(true);
  };

  const onFolderCreated = () => {
    setShowCreateFolderModal(false);
    tableRef?.current?.refresh();
  };

  const handleUploadFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      uploadFiles(Array.from(files));

      input.remove();
    };
    input.click();
  };

  const handleUploadFolder = (e: any) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.webkitdirectory = true;
    input.style.display = "none";
    input.onchange = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;

      uploadFolder(Array.from(files));

      input.remove();
    };
    input.click();
  };

  const handleDownload = (rowData: CaseFile) => {
    if (rowData.is_folder) {
      FilesAPI.downloadFiles({
        uuids: [rowData.uuid],
        tenant_slug: workspace?.tenant_slug,
        parent_uuid: folder_id,
      });
    } else {
      const downloadUrl = rowData?.link?.replace(
        "localhost:3000",
        "localhost:3001"
      ); // replace is used for local development

      if (downloadUrl) {
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = rowData.name;
        anchor.click();
        anchor.remove();

        LoggingAPI.createLog({
          case_id: caseInfo.case_id,
          user_id: currentUser.user_id,
          message: `Downloaded File`,
          event: "case_file:download",
          object_type: "file",
          object_uuid: rowData.uuid,
        });
      }
    }
  };

  const handleViewitem = (rowData: CaseFile) => {
    handleFileClick(rowData);
  };

  const handleUpdateFile = (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShowEditFileModal(true);
  };

  const handleDeleteFile = (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShowDeleteFileModal(true);
  };

  const handleShareViaRelay = (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShowShareModal(true);
  };

  const handleRowActionButtonClick = (rowData: CaseFile) => {
    setSelectedItem(rowData);
    setShowInfoPanel(true);
  };

  const onProgress = (e: { detail: UploadProgress }) => {
    const { detail } = e;
    if (detail.type === "complete") {
      tableRef?.current?.refresh();
    }
  };

  // Register for file upload progress events
  useEffect(() => {
    document.addEventListener(
      "file-uploader:file-progress",
      onProgress as unknown as EventListener
    );
    return () => {
      document.removeEventListener(
        "file-uploader:file-progress",
        onProgress as unknown as EventListener
      );
    };
  }, []);

  // log access to folders
  useEffect(() => {
    if (folder_id) {
      LoggingAPI.createLog({
        case_id: caseInfo.case_id,
        user_id: currentUser.user_id,
        message: `Accessed folder`,
        event: "case_folder:access",
        object_type: "folder",
        object_uuid: folder_id,
      });
    }
  }, [folder_id]);

  return (
    <>
      <div
        style={{
          marginBottom: "10px",
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <BreadCrumbs />
      </div>
      <EvidenceFilesTreeBanner />
      <CaseFilesTable
        tableRef={tableRef}
        query={{
          case_uuid: caseInfo.uuid,
          parent_uuid: folder_id || "root",
        }}
        onCreateFolder={handleCreateFolder}
        onUploadFile={handleUploadFile}
        onUploadFolder={handleUploadFolder}
        onActionButtonClick={handleRowActionButtonClick}
        onShowDetails={handleRowActionButtonClick}
        onDownload={handleDownload}
        onView={handleViewitem}
        onUpdate={handleUpdateFile}
        onDelete={handleDeleteFile}
        onShareViaRelay={handleShareViaRelay}
        onSearch={setSearch}
      />
      {showInfoPanel && selectedItem && (
        <CaseFileFlyout
          open={showInfoPanel}
          onClose={() => setShowInfoPanel(false)}
          onDelete={() => {
            setShowInfoPanel(false);
            setShowDeleteFileModal(false);
          }}
          defaultData={{
            ...selectedItem,
            case: {
              uuid: caseInfo.uuid,
              case_id: caseInfo.case_id,
              case_name: "",
              case_number: "",
            },
          }}
        />
      )}
      {showCreateFolderModal && (
        <CreateCaseFolderModal
          show={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onCancel={() => setShowCreateFolderModal(false)}
          onSubmit={onFolderCreated}
          defaultFormData={{
            case_uuid: caseInfo.uuid,
            case_id: caseInfo.case_id,
            parent_uuid: folder_id || "root",
          }}
        />
      )}
      {showEditFileModal && selectedItem && (
        <EditCaseFileModal
          show={showEditFileModal}
          onCancel={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
          }}
          onClose={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
          }}
          onSubmit={() => {
            setShowEditFileModal(false);
            setSelectedItem(null);
            tableRef?.current?.refresh();
          }}
          defaultFormData={selectedItem}
        />
      )}
      {showDeleteFileModal && selectedItem && (
        <DeleteCaseFileModal
          show={showDeleteFileModal}
          onClose={closeDeleteFileModal}
          onCancel={closeDeleteFileModal}
          onSubmit={() => {
            closeDeleteFileModal();
            tableRef?.current?.refresh();
          }}
          defaultFormData={selectedItem}
        />
      )}
      {showShareModal && selectedItem && (
        <ShareItemModal
          show={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSubmit={(values) => {
            setShowShareModal(false);
          }}
          onCancel={() => setShowShareModal(false)}
          defaultValues={{
            selectedItems: [selectedItem],
            caseInfo: { uuid: caseInfo.uuid, case_id: caseInfo.case_id },
          }}
        />
      )}
    </>
  );
};

export const DocumentsPage = () => {
  const { case_id } = useParams();
  const tableRef = useRef<CaseFilesTableRef>();
  const { data: caseInfo } = useQuery({
    queryKey: [
      "cases",
      "list",
      {
        case_id: case_id ? parseInt(case_id) : null,
      },
    ],
    queryFn: () =>
      CasesApi.getCases({
        case_id: case_id ? parseInt(case_id) : null,
      }),
    enabled: !!case_id,
    select: (data) => data?.[0] || {},
  });

  if (!caseInfo) return <div>Error loading case documents</div>;

  return (
    <CaseFilesProvider
      case_id={caseInfo.case_id}
      case_uuid={caseInfo.uuid}
      tableRef={tableRef as MutableRefObject<CaseFilesTableRef>}
    >
      <CaseDocumentsWrapper caseInfo={caseInfo} />
    </CaseFilesProvider>
  );
};
